/**
 * The revenue chart of slide 5 ("Início", NEWS-32): a smoothed curve of the
 * closed days with its area, the last day (today, so far) as a separate
 * dashed piece, and where the label of a campaign's day sits. Pure; geometry
 * of the prototype (artifact v5).
 */

export interface ChartPoint {
  x: number;
  y: number;
}

export interface RevenuePath {
  points: ChartPoint[];
  /** Smoothed curve through every point but the last. */
  mainPath: string;
  /** `mainPath` closed down to the baseline, for the gradient. */
  areaPath: string;
  /** From the second-to-last point to the last one (dashed). */
  partialPath: string;
  /** Baseline (bottom of the plot) in SVG units. */
  baselineY: number;
  /** Position of a day's point in % of the box (for an HTML label). */
  labelAt: (index: number) => { xPct: number; yPct: number };
}

const PAD_X = 4;
const BOTTOM = 18;
const TOP_ROOM = 30;
const HEADROOM = 1.12;

const fmt = (n: number) => n.toFixed(1);

function smooth(points: ChartPoint[]): string {
  return points.reduce((d, pt, i) => {
    if (i === 0) return `M${fmt(pt.x)} ${fmt(pt.y)}`;
    const prev = points[i - 1];
    const cx = (prev.x + pt.x) / 2;
    return `${d} C${fmt(cx)} ${fmt(prev.y)} ${fmt(cx)} ${fmt(pt.y)} ${fmt(pt.x)} ${fmt(pt.y)}`;
  }, '');
}

export function revenuePath(
  series: readonly number[],
  width: number,
  height: number
): RevenuePath {
  const max = Math.max(...series, 0) * HEADROOM || 1;
  const step = (width - PAD_X * 2) / Math.max(1, series.length - 1);
  const baselineY = height - BOTTOM;
  const points = series.map((v, i) => ({
    x: PAD_X + i * step,
    y: baselineY - (v / max) * (height - TOP_ROOM),
  }));
  const closed = points.slice(0, -1);
  const mainPath = smooth(closed);
  const first = closed[0];
  const lastClosed = closed[closed.length - 1];
  const areaPath =
    closed.length > 0
      ? `${mainPath} L${fmt(lastClosed.x)} ${baselineY} L${fmt(first.x)} ${baselineY} Z`
      : '';
  return {
    points,
    mainPath,
    areaPath,
    partialPath: smooth(points.slice(-2)),
    baselineY,
    labelAt: (index) => ({
      xPct: (points[index].x / width) * 100,
      yPct: (points[index].y / height) * 100,
    }),
  };
}
